import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import blogApi, { blogImageUrl } from "../../apiProvider/blogApi";
import { useTranslation } from "../../context/TranslationContext";
import "./blog.css";

const stripHtml = (html = "") => html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { translateSync } = useTranslation();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    blogApi.get(slug).then((data) => {
      setBlog(data);
      setLoading(false);
      blogApi.list({ page: 0, limit: 4 }).then((list) => {
        setRelated((list?.data || []).filter((item) => item.slug !== slug).slice(0, 3));
      });
    }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container" style={{ paddingTop: 160, minHeight: 420 }}>Loading blog...</div>;
  if (!blog) return <div className="container" style={{ paddingTop: 160, minHeight: 420 }}>Blog not found.</div>;

  const url = `https://coloursboutique.com/blog/${blog.slug}`;
  const image = blogImageUrl(blog.coverImage) || blog.ogImage;
  const description = blog.metaDescription || blog.excerpt || stripHtml(blog.contentHtml).slice(0, 155);
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blog.metaTitle || blog.title,
    "description": description,
    "image": image ? [image] : [],
    "datePublished": blog.publishedAt,
    "dateModified": blog.updatedAt,
    "author": { "@type": "Organization", "name": blog.authorName || "Colours Boutique" },
    "publisher": { "@type": "Organization", "name": "Colours Boutique" },
    "mainEntityOfPage": { "@type": "WebPage", "@id": url }
  };

  return (
    <>
      <Helmet>
        <title>{blog.metaTitle || `${blog.title} | Colours Boutique`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={blog.canonicalUrl || url} />
        <meta property="og:title" content={blog.ogTitle || blog.metaTitle || blog.title} />
        <meta property="og:description" content={blog.ogDescription || description} />
        {image && <meta property="og:image" content={image} />}
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div className="blog-page-shell">
        <section className="blog-hero">
          <div className="container">
            <p className="blog-eyebrow">Colours Boutique Journal</p>
            <h1 className="blog-hero-title">{translateSync(blog.title)}</h1>
            <nav aria-label="breadcrumb" className="boutique-breadcrumb-nav center-align">
              <ol className="boutique-breadcrumb-ol boutique-breadcrumb-center">
                <li className="breadcrumb-item"><span onClick={() => navigate("/")}>{translateSync("Home")}</span></li>
                <li className="breadcrumb-item"><span onClick={() => navigate("/blog")}>{translateSync("Blog")}</span></li>
                <li className="breadcrumb-item active">{translateSync(blog.category?.name || "Guide")}</li>
              </ol>
            </nav>
          </div>
        </section>

        <section className="blog-article-section">
          <div className="container blog-reading-container">
            {image && <img src={image} alt={blog.title} className="blog-cover-image" />}
            <div className="blog-meta">
              <span>{blog.category?.name || "Saree Guide"}</span>
              {blog.publishedAt && <span>{new Date(blog.publishedAt).toLocaleDateString()}</span>}
              {blog.focusKeyword && <span>{blog.focusKeyword}</span>}
            </div>
            {blog.excerpt && <p className="blog-intro">{translateSync(blog.excerpt)}</p>}
            <article className="blog-content" dangerouslySetInnerHTML={{ __html: blog.contentHtml }} />

            {related.length > 0 && (
              <aside className="blog-related-guides">
                <h2>{translateSync("Related Guides")}</h2>
                <div className="row g-4">
                  {related.map((item) => (
                    <div className="col-md-4" key={item._id}>
                      <button className="blog-related-card" onClick={() => navigate(`/blog/${item.slug}`)}>
                        <img src={blogImageUrl(item.coverImage) || "/assets/images/logo.png"} alt={item.title} />
                        <strong>{translateSync(item.title)}</strong>
                      </button>
                    </div>
                  ))}
                </div>
              </aside>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogDetailPage;
